import type { EnhancedScopeData } from '@ardoq/data-model';
import { enhancedScopeDataOperations } from '@ardoq/scope-data';
import type { ArrangeableNode, RelatedNode } from './compileArrangement';
import { getComponentTypeName } from './componentTypeName';

/** The node fields a recipe reads, shared by the aq-blocks and Interactive Blocks trees. */
export type ArrangeableTreeNode = {
  id: string;
  modelId: string | null;
  labels: string[];
  children: ArrangeableTreeNode[] | null;
};

type ComponentReference = {
  otherComponentId: string;
  referenceType: string;
  outgoing: boolean;
};

const referencesByComponentCache = new WeakMap<
  EnhancedScopeData,
  Map<string, ComponentReference[]>
>();

const indexReferences = (
  scopeData: EnhancedScopeData
): Map<string, ComponentReference[]> => {
  const index = new Map<string, ComponentReference[]>();
  const add = (componentId: string, entry: ComponentReference) => {
    const entries = index.get(componentId);
    if (entries) entries.push(entry);
    else index.set(componentId, [entry]);
  };
  for (const reference of scopeData.references) {
    const referenceType =
      enhancedScopeDataOperations.getReferenceType(scopeData, reference)
        ?.name ?? '';
    add(reference.source, {
      otherComponentId: reference.target,
      referenceType,
      outgoing: true,
    });
    add(reference.target, {
      otherComponentId: reference.source,
      referenceType,
      outgoing: false,
    });
  }
  return index;
};

/** Every component's references, indexed once per scope data. */
const referencesByComponent = (
  scopeData: EnhancedScopeData
): Map<string, ComponentReference[]> => {
  const cached = referencesByComponentCache.get(scopeData);
  if (cached) return cached;
  const index = indexReferences(scopeData);
  referencesByComponentCache.set(scopeData, index);
  return index;
};

const subtreeModelIds = (node: ArrangeableTreeNode): string[] => [
  ...(node.modelId ? [node.modelId] : []),
  ...(node.children ?? []).flatMap(subtreeModelIds),
];

type SiblingLookup = {
  references: Map<string, ComponentReference[]>;
  /** The components in each sibling's subtree, its own first. */
  subtrees: Map<string, string[]>;
  /** The sibling each component in the siblings' subtrees belongs to. */
  siblingIdByModelId: Map<string, string>;
  /** The component each sibling itself represents, if any. */
  ownModelIdBySiblingId: Map<string, string | null>;
};

const toSiblingLookup = (
  scopeData: EnhancedScopeData,
  siblings: ArrangeableTreeNode[]
): SiblingLookup => {
  const subtrees = new Map(
    siblings.map(sibling => [sibling.id, subtreeModelIds(sibling)] as const)
  );
  return {
    references: referencesByComponent(scopeData),
    subtrees,
    siblingIdByModelId: new Map(
      [...subtrees].flatMap(([siblingId, modelIds]) =>
        modelIds.map(modelId => [modelId, siblingId] as const)
      )
    ),
    ownModelIdBySiblingId: new Map(
      siblings.map(sibling => [sibling.id, sibling.modelId] as const)
    ),
  };
};

export type ReferenceTypeCount = { name: string; count: number };

/** How many references of each type connect two of these siblings, or anything inside them. */
export const countSiblingReferenceTypes = (
  scopeData: EnhancedScopeData,
  siblings: ArrangeableTreeNode[]
): ReferenceTypeCount[] => {
  const { references, subtrees, siblingIdByModelId } = toSiblingLookup(
    scopeData,
    siblings
  );
  const counts = [...subtrees]
    .flatMap(([siblingId, modelIds]) =>
      modelIds.flatMap(modelId =>
        (references.get(modelId) ?? []).filter(
          ({ otherComponentId, outgoing }) => {
            const otherSiblingId = siblingIdByModelId.get(otherComponentId);
            return outgoing && otherSiblingId && otherSiblingId !== siblingId;
          }
        )
      )
    )
    .reduce(
      (acc, { referenceType }) =>
        acc.set(referenceType, (acc.get(referenceType) ?? 0) + 1),
      new Map<string, number>()
    );
  return [...counts].map(([name, count]) => ({ name, count }));
};

const relationKey = ({
  nodeId,
  referenceType,
  nested,
  outgoing,
}: RelatedNode): string =>
  `${nodeId}\u0000${referenceType}\u0000${nested}\u0000${outgoing}`;

/**
 * The siblings connected by a reference to this node or anything inside it, each counted for the
 * sibling that holds the other end: the block itself, or the group it sits in.
 */
const relatedSiblings = (
  { references, siblingIdByModelId, ownModelIdBySiblingId }: SiblingLookup,
  { nodeId, modelIds }: { nodeId: string; modelIds: string[] }
): RelatedNode[] => {
  const related = modelIds.flatMap(modelId =>
    (references.get(modelId) ?? []).flatMap(
      ({ otherComponentId, referenceType, outgoing }) => {
        const siblingId = siblingIdByModelId.get(otherComponentId);
        return siblingId && siblingId !== nodeId
          ? [
              {
                nodeId: siblingId,
                referenceType,
                outgoing,
                nested:
                  ownModelIdBySiblingId.get(siblingId) !== otherComponentId,
              },
            ]
          : [];
      }
    )
  );
  return [
    ...new Map(related.map(entry => [relationKey(entry), entry])).values(),
  ];
};

const MAX_ANCESTOR_DEPTH = 32;
const MAX_HIERARCHY_ROOTS = 20;
const MAX_ROOT_CHILD_LABELS = 25;

export type HierarchyRoot = {
  label: string;
  descendantCount: number;
  childLabels: string[];
};

const parentComponentId = (
  scopeData: EnhancedScopeData,
  componentId: string
): string | null => scopeData.componentsById[componentId]?.parent ?? null;

/** Nearest first; stops at a cycle, a component outside the scope, or the depth cap. */
const ancestorComponentIds = (
  scopeData: EnhancedScopeData,
  componentId: string
): string[] => {
  const walk = (id: string | null, seen: string[]): string[] =>
    id === null || seen.includes(id) || seen.length >= MAX_ANCESTOR_DEPTH
      ? seen
      : walk(parentComponentId(scopeData, id), [...seen, id]);
  return walk(parentComponentId(scopeData, componentId), []);
};

const isString = (value: string | undefined): value is string =>
  typeof value === 'string';

export const findNode = <T extends ArrangeableTreeNode>(
  node: T,
  id: string
): T | null => {
  if (node.id === id) return node;
  for (const child of (node.children ?? []) as T[]) {
    const found = findNode(child, id);
    if (found) return found;
  }
  return null;
};

/**
 * Siblings in the form a region matches on. `parentId` is the nearest ancestor
 * in the component hierarchy that is also one of these siblings, which is how a
 * flat view still knows its tree.
 */
export const toArrangeableNodes = (
  scopeData: EnhancedScopeData,
  siblings: ArrangeableTreeNode[]
): ArrangeableNode[] => {
  const nodeIdByModelId = new Map(
    siblings.flatMap(sibling =>
      sibling.modelId ? [[sibling.modelId, sibling.id] as const] : []
    )
  );
  const lookup = toSiblingLookup(scopeData, siblings);
  return siblings.map(node => {
    const ancestors = node.modelId
      ? ancestorComponentIds(scopeData, node.modelId)
      : [];
    return {
      id: node.id,
      label: node.labels[0] ?? '',
      componentType: getComponentTypeName(scopeData, node),
      isGroup: Boolean(node.children),
      parentId:
        ancestors
          .map(ancestorId => nodeIdByModelId.get(ancestorId))
          .find(isString) ?? null,
      ancestorLabels: ancestors
        .map(ancestorId => scopeData.componentsById[ancestorId]?.name)
        .filter(isString),
      related: relatedSiblings(lookup, {
        nodeId: node.id,
        modelIds: lookup.subtrees.get(node.id) ?? [],
      }),
    };
  });
};

const countDescendants = (
  childrenByParentId: Map<string | null, ArrangeableNode[]>,
  node: ArrangeableNode
): number =>
  (childrenByParentId.get(node.id) ?? []).reduce(
    (sum, child) => sum + 1 + countDescendants(childrenByParentId, child),
    0
  );

/** The tops of the component hierarchy among these siblings, largest first. */
export const toHierarchyRoots = (nodes: ArrangeableNode[]): HierarchyRoot[] => {
  const childrenByParentId = Map.groupBy(nodes, node => node.parentId);
  return (childrenByParentId.get(null) ?? [])
    .map(root => ({
      label: root.label,
      descendantCount: countDescendants(childrenByParentId, root),
      childLabels: (childrenByParentId.get(root.id) ?? [])
        .slice(0, MAX_ROOT_CHILD_LABELS)
        .map(child => child.label),
    }))
    .filter(root => root.descendantCount > 0)
    .sort((a, b) => b.descendantCount - a.descendantCount)
    .slice(0, MAX_HIERARCHY_ROOTS);
};
